/**
 * Modal to create a new license via dual-gate validation flow.
 *
 * Extracted from LicenseManagementPage.tsx (Sub-PR 6.3).
 */

import type { LicenseCriteria, Organisation, XeroInvoiceState } from '../../types'
import { CreateLicenseCriteriaStep } from './CreateLicenseCriteriaStep'
import { CreateLicenseGateCards } from './CreateLicenseGateCards'
import { CreateLicenseInvoiceStep } from './CreateLicenseInvoiceStep'
import {
  LicenseGateRequiredWarning,
  LicenseSummaryCard,
  PaymentGateRequiredWarning,
} from './CreateLicenseSummaryAndWarnings'
import { useCreateLicenseGatesState } from './use-create-license-gates-state'

interface CreateLicenseFromGatesModalProps {
  organisations: Organisation[]
  xeroInvoices: XeroInvoiceState[]
  licenseCriteria: LicenseCriteria[]
  onClose: () => void
  onCreate: (orgId: string, xeroId: string, criteriaId: string) => Promise<void>
  onRefresh: () => Promise<void>
}

function ModalHeader({ onClose }: { onClose: () => void }): React.ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 dark:border-stone-700">
      <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
        Create License from Verified Data
      </h2>
      <button
        onClick={onClose}
        className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}

interface OrganisationSelectProps {
  organisations: Organisation[]
  selectedOrgId: string
  onChange: (id: string) => void
}

function OrganisationSelect({
  organisations,
  selectedOrgId,
  onChange,
}: OrganisationSelectProps): React.ReactElement {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
        1. Select Organisation
      </label>
      <select
        value={selectedOrgId}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
      >
        <option value="">Select organisation...</option>
        {organisations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
    </div>
  )
}

interface ModalActionsProps {
  onClose: () => void
  onCreate: () => void
  canCreate: boolean
  creating: boolean
}

function ModalActions({
  onClose,
  onCreate,
  canCreate,
  creating,
}: ModalActionsProps): React.ReactElement {
  return (
    <div className="flex justify-end gap-3 border-t border-stone-200 pt-4 dark:border-stone-700">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        Cancel
      </button>
      <button
        onClick={onCreate}
        disabled={!canCreate || creating}
        className="rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {creating ? 'Creating...' : 'Create License'}
      </button>
    </div>
  )
}

interface OrgFlowProps {
  state: ReturnType<typeof useCreateLicenseGatesState>
  xeroInvoices: XeroInvoiceState[]
  licenseCriteria: LicenseCriteria[]
}

function OrgFlow({ state, xeroInvoices, licenseCriteria }: OrgFlowProps): React.ReactElement {
  return (
    <>
      <CreateLicenseGateCards
        hasPaymentGate={state.hasPaymentGate}
        hasLicenseGate={state.hasLicenseGate}
        paymentGateMessage={state.paymentGateMessage}
        licenseGateMessage={state.licenseGateMessage}
      />
      {state.hasPaymentGate && (
        <CreateLicenseInvoiceStep
          selectedXeroId={state.selectedXeroId}
          setSelectedXeroId={state.setSelectedXeroId}
          availableInvoices={state.availableInvoices}
          usedInvoices={state.usedInvoices}
          selectedXero={state.selectedXero}
        />
      )}
      {state.selectedXeroId && (
        <CreateLicenseCriteriaStep
          linkedCriteria={state.linkedCriteria}
          linkableCriteria={state.linkableCriteria}
          selectedCriteriaToLink={state.selectedCriteriaToLink}
          setSelectedCriteriaToLink={state.setSelectedCriteriaToLink}
          linking={state.linking}
          onLinkCriteria={() => {
            void state.handleLinkCriteria()
          }}
        />
      )}
      {state.canCreate && state.linkedCriteria && (
        <LicenseSummaryCard selectedOrg={state.selectedOrg} linkedCriteria={state.linkedCriteria} />
      )}
      {!state.hasPaymentGate && (
        <PaymentGateRequiredWarning
          xeroInvoices={xeroInvoices}
          selectedOrgId={state.selectedOrgId}
        />
      )}
      {!state.hasLicenseGate && (
        <LicenseGateRequiredWarning
          licenseCriteria={licenseCriteria}
          selectedOrgId={state.selectedOrgId}
        />
      )}
    </>
  )
}

export function CreateLicenseFromGatesModal(
  props: CreateLicenseFromGatesModalProps
): React.ReactElement {
  const { organisations, xeroInvoices, licenseCriteria, onClose, onCreate, onRefresh } = props
  const state = useCreateLicenseGatesState({
    organisations,
    xeroInvoices,
    licenseCriteria,
    onCreate,
    onRefresh,
    onClose,
  })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <ModalHeader onClose={onClose} />
        <div className="space-y-6 p-6">
          {state.error !== null && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {state.error}
            </div>
          )}
          <OrganisationSelect
            organisations={organisations}
            selectedOrgId={state.selectedOrgId}
            onChange={state.handleOrgChange}
          />
          {state.selectedOrgId && (
            <OrgFlow state={state} xeroInvoices={xeroInvoices} licenseCriteria={licenseCriteria} />
          )}
          <ModalActions
            onClose={onClose}
            onCreate={() => {
              void state.handleCreate()
            }}
            canCreate={state.canCreate}
            creating={state.creating}
          />
        </div>
      </div>
    </div>
  )
}
